import {useCallback, useEffect, useRef, useState} from 'react';
import {Slide, SlidingWizard, SlidingWizardHandle} from '../../components/sliding_form_wizard/sliding_wizard.tsx';
import {flushSync} from 'react-dom';
import styles from './appointment.module.scss';
import {Form, useSearchParams} from 'react-router-dom';
import {VehicleSelectContent, VehicleSelectFooter, VehicleSelectTitle} from './components/vehicle_select.tsx';
import {useAppSelector} from '../../store/store.ts';
import {LocationSelectContent, LocationSelectFooter, LocationSelectTitle} from './components/location_select.tsx';
import {
    AppointmentDetailsContent,
    AppointmentDetailsFooter,
    AppointmentDetailsTitle,
} from './components/appointment_details.tsx';
import {DatePickerContent, DatePickerFooter, DatePickerTitle} from './components/date_picker.tsx';
import {Main} from '../../components/main/main.tsx';
import {addMonths, endOfMonth, format, startOfMonth} from 'date-fns';
import {Planner} from '../../network/planner.ts';
import {AvailabilityType, Customer} from '../../gql/pwa/graphql.ts';
import {Vehicle} from '../../gql/customer/graphql.ts';
import {MileageContent, MileageFooter, MileageTitle} from './components/mileage.tsx';
import {SummaryContent, SummaryFooter, SummaryTitle} from './components/summary.tsx';
import {TimePickerContent, TimePickerFooter, TimePickerTitle} from './components/time_picker.tsx';
import {SelectVehicleModal} from './components/modals/select_vehicle_modal.tsx';
import {SelectLocationModal} from './components/modals/select_location_modal.tsx';
import {ReviewPrefilledInfoModal} from './components/modals/review_prefilled_info_modal.tsx';

function initialVehicle(vehicles: (Vehicle | null)[], defaultVehicleId: number | undefined): Vehicle | null {
    return vehicles?.find((vehicle) => vehicle?.id === defaultVehicleId) || vehicles[0];
}

export function Appointment() {
    const appointmentSliderRef = useRef<SlidingWizardHandle>(null);
    const [isEditing, setIsEditing] = useState(false);

    const onPreviousSlide = useCallback(() => {
        if (isEditing) {
            flushSync(() => setIsEditing(false));
        }
        appointmentSliderRef.current?.slidePrev();
    }, [appointmentSliderRef, isEditing, setIsEditing]);
    const onNextSlide = useCallback(() => {
        if (isEditing) {
            flushSync(() => setIsEditing(false));
        }
        appointmentSliderRef.current?.slideNext();
    }, [appointmentSliderRef, isEditing, setIsEditing]);

    const [params] = useSearchParams();
    const urlVehicleId = params.get('vehicle_id');
    const urlLocationId = params.get('location_id');

    const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
    const [isSelectLocationModalOpen, setIsSelectLocationModalOpen] = useState(false);
    const [isReviewPrefilledInfoModalOpen, setIsReviewPrefilledInfoModalOpen] = useState<boolean>(
        urlVehicleId !== null || urlLocationId !== null,
    );

    const customerInfo = useAppSelector((state) => state.customerInfo);
    const appConfig = useAppSelector((state) => state.appConfig);
    const customer = customerInfo.me?.customer as Customer;
    const vehicles = (customer?.vehicles as Vehicle[]) || [];

    const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(
        initialVehicle(
            vehicles || [],
            (urlVehicleId && Number.parseInt(urlVehicleId, 10)) || customer?.default_vehicle_id?.valueOf(),
        )?.id || null,
    );
    const locations = appConfig.locations;

    const location =
        locations?.find(
            (location) =>
                location?.id ===
                ((urlLocationId && Number.parseInt(urlLocationId, 10)) || customer?.default_location_id),
        ) ||
        locations?.[0] ||
        null;

    const [locationId, setLocationId] = useState<number | null>(location?.id || null);
    const selectedLocation = locations?.find((location) => location?.id === locationId) || null;
    const specialty = selectedLocation?.specialties?.find(() => true);
    const [remarks, setRemarks] = useState('');
    const [mainActivityId, setMainActivityId] = useState<string | null>(null);
    const [subActivityIds, setSubActivityIds] = useState<string[]>([]);
    const [replacementVehicleId, setReplacementVehicleId] = useState<string | null>(null);
    const [availabilities, setAvailabilities] = useState<AvailabilityType[]>([]);
    const [availableTimes, setAvailableTimes] = useState<string[]>([]);
    const [appointmentDate, setAppointmentDate] = useState<Date | null>(null);
    const [time, setTime] = useState<string | null>(null);

    const selectedVehicle = vehicles?.find((vehicle) => vehicle?.id === selectedVehicleId) || null;
    const selectedVehicleLicensePlate = selectedVehicle?.identifiers?.find((v) => v?.type === 'license_plate')?.value;
    const selectedVehicleMileage = useCallback(() => {
        return selectedVehicle?.mileage?.valueOf() || null;
    }, [selectedVehicle]);
    const [mileage, setMileage] = useState<number | null>(selectedVehicleMileage());
    useEffect(() => {
        setMileage(selectedVehicleMileage());
    }, [setMileage, selectedVehicleId, selectedVehicleMileage]);

    const onAppointmentChange = useCallback(async () => {
        if (!locationId || !mainActivityId || !replacementVehicleId) {
            return;
        }
        const now = new Date();
        const startDate = startOfMonth(now);
        const endDate = endOfMonth(addMonths(startDate, 3));
        const availabilities = await Planner.availabilities(
            locationId,
            startDate,
            endDate,
            mainActivityId,
            replacementVehicleId,
            subActivityIds,
        );
        const plannerLocation = availabilities.data.locations && availabilities.data.locations[0];
        const specialty = plannerLocation?.specialties && plannerLocation.specialties[0];
        if (specialty?.availabilities) {
            setAvailabilities(specialty.availabilities as AvailabilityType[]);
        }
    }, [locationId, mainActivityId, subActivityIds, replacementVehicleId]);
    const getTimeAvailabilities = useCallback(
        async (selectedDate?: Date) => {
            const date = selectedDate || appointmentDate;
            if (!locationId || !mainActivityId || !replacementVehicleId || !date) {
                return;
            }
            const availableTimes = await Planner.availableTimes(locationId, date, mainActivityId, subActivityIds);
            const plannerLocation = availableTimes.data.locations && availableTimes.data.locations[0];
            const specialty = plannerLocation?.specialties && plannerLocation.specialties[0];
            if (specialty?.available_times) {
                setAvailableTimes(specialty.available_times as string[]);
            }
        },
        [appointmentDate, locationId, mainActivityId, subActivityIds, replacementVehicleId],
    );
    const onAppointmentDateChange = useCallback(
        (date: Date) => {
            setAppointmentDate(date);
            void getTimeAvailabilities(date);
        },
        [getTimeAvailabilities],
    );

    useEffect(() => {
        void onAppointmentChange();
    }, [onAppointmentChange, locationId, mainActivityId, replacementVehicleId, subActivityIds]);

    //
    // Start Slide definitions
    //
    const vehicleSelectSlide: Slide = {
        onPreviousClick: null,
        title: <VehicleSelectTitle brand={selectedVehicle?.brand} />,
        content: (
            <VehicleSelectContent
                isEditing={isSelectVehicleModalOpen}
                vehicles={vehicles}
                vehicleId={selectedVehicleId}
            />
        ),
        footer: (
            <VehicleSelectFooter
                isDisabled={vehicles === null || vehicles.length === 0}
                isEditing={isEditing}
                setIsEditing={() => setIsSelectVehicleModalOpen(true)}
                onNextSlide={onNextSlide}
                vehicles={vehicles}
            />
        ),
        clearBackground: true,
    };

    const locationSelectSlide: Slide = {
        onPreviousClick: onPreviousSlide,
        title: <LocationSelectTitle locationId={locationId} />,
        content: <LocationSelectContent isEditing={isEditing} locationId={locationId} />,
        footer: (
            <LocationSelectFooter
                isEditing={isEditing}
                setIsEditing={() => setIsSelectLocationModalOpen(true)}
                onPrevSlide={onPreviousSlide}
                onNextSlide={() => {
                    setIsEditing(true);
                    onNextSlide();
                }}
            />
        ),
        clearBackground: true,
    };

    const appointmentSlides: Slide[] = [
        {
            onPreviousClick: onPreviousSlide,
            title: <AppointmentDetailsTitle />,
            content: (
                <AppointmentDetailsContent
                    onMainActivityIdChange={setMainActivityId}
                    onyReplacementVehicleIdChange={setReplacementVehicleId}
                    onSubActivityIdsChange={setSubActivityIds}
                    mainActivityId={mainActivityId}
                    subActivityIds={subActivityIds}
                    replacementVehicleId={replacementVehicleId}
                    locationId={locationId}
                    remarks={remarks}
                    onRemarksChange={setRemarks}
                    onEditClick={() => setIsEditing(true)}
                    isEditing={isEditing}
                />
            ),
            disableNextSlide: mainActivityId === null || replacementVehicleId === null,
            footer: (
                <AppointmentDetailsFooter
                    onPreviousSlide={onPreviousSlide}
                    onNextSlide={onNextSlide}
                    nextIsDisabled={mainActivityId === null || replacementVehicleId === null}
                />
            ),
        },
        {
            onPreviousClick: onPreviousSlide,
            title: <DatePickerTitle />,
            content: (
                <DatePickerContent
                    availabilities={availabilities}
                    date={appointmentDate}
                    onDateChange={(date) => onAppointmentDateChange(date)}
                />
            ),
            disableNextSlide: appointmentDate === null,
            footer: (
                <DatePickerFooter
                    onPreviousSlide={onPreviousSlide}
                    onNextSlide={() => {
                        void getTimeAvailabilities();
                        onNextSlide();
                    }}
                    nextIsDisabled={appointmentDate === null}
                />
            ),
        },
        {
            onPreviousClick: onPreviousSlide,
            title: <TimePickerTitle />,
            content: (
                <TimePickerContent
                    availableTimes={availableTimes}
                    time={time}
                    onTimeChange={setTime}
                    isEditing={isEditing}
                    onEditClick={() => setIsEditing(true)}
                />
            ),
            disableNextSlide: time === null,
            footer: (
                <TimePickerFooter
                    onPreviousSlide={onPreviousSlide}
                    onNextSlide={onNextSlide}
                    nextIsDisabled={time === null}
                />
            ),
        },
        {
            onPreviousClick: onPreviousSlide,
            title: <MileageTitle />,
            disableNextSlide: mileage === null,
            content: <MileageContent mileage={mileage} brand={selectedVehicle?.brand} onMileageChange={setMileage} />,
            footer: (
                <MileageFooter
                    onPreviousSlide={onPreviousSlide}
                    onNextSlide={onNextSlide}
                    nextIsDisabled={mileage === null}
                />
            ),
        },
        {
            onPreviousClick: onPreviousSlide,
            title: <SummaryTitle />,
            content: (
                <SummaryContent
                    locationId={locationId}
                    vehicle={selectedVehicle}
                    mileage={mileage}
                    mainActivityId={mainActivityId}
                    subActivityIds={subActivityIds}
                    replacementVehicleId={replacementVehicleId}
                    date={appointmentDate}
                    time={time}
                    remarks={remarks}
                />
            ),
            footer: <SummaryFooter onPreviousSlide={onPreviousSlide} />,
        },
    ];

    //
    // End Slide definitions
    //

    const slides = [vehicleSelectSlide];
    if (locations && locations.length > 1) {
        slides.push(locationSelectSlide);
    }
    slides.push(...appointmentSlides);

    return (
        <Main>
            {isSelectVehicleModalOpen && (
                <SelectVehicleModal
                    onCloseModal={() => setIsSelectVehicleModalOpen(false)}
                    vehicles={vehicles}
                    vehicleId={selectedVehicleId}
                    onVehicleIdChange={setSelectedVehicleId}
                />
            )}
            {isSelectLocationModalOpen && (
                <SelectLocationModal
                    onCloseModal={() => setIsSelectLocationModalOpen(false)}
                    locations={locations}
                    locationId={locationId}
                    onLocationIdChange={setLocationId}
                />
            )}
            {isReviewPrefilledInfoModalOpen && selectedVehicle && location && (
                <ReviewPrefilledInfoModal
                    onCloseModal={() => setIsReviewPrefilledInfoModalOpen(false)}
                    onAccept={() => {
                        onNextSlide();
                        onNextSlide();
                        setIsReviewPrefilledInfoModalOpen(false);
                    }}
                    vehicle={selectedVehicle}
                    location={location}
                />
            )}
            <div className={styles.appointment}>
                <Form method="post" className={styles.appointment_content}>
                    <SlidingWizard
                        isEditing={isEditing || vehicles === null || vehicles.length === 0}
                        ref={appointmentSliderRef}
                        slides={slides}
                    />
                    <input name="location_id" type="hidden" value={locationId || ''} />
                    <input name="specialty_id" type="hidden" value={specialty?.id || ''} />
                    <input name="vehicle_id" type="hidden" value={selectedVehicleId || ''} />
                    <input name="license_plate" type="hidden" value={selectedVehicleLicensePlate || ''} />
                    <input name="mileage" type="hidden" value={mileage || ''} />
                    <input name="main_activity_id" type="hidden" value={mainActivityId || ''} />
                    <input name="sub_activity_ids" type="hidden" value={subActivityIds || ''} />
                    <input name="replacement_vehicle_id" type="hidden" value={replacementVehicleId || ''} />
                    <input name="remarks" type="hidden" value={remarks} />
                    <input
                        name="date"
                        type="hidden"
                        value={(appointmentDate && format(appointmentDate, 'yyyy-MM-dd')) || ''}
                    />
                    <input name="time" type="hidden" value={time || ''} />
                </Form>
            </div>
        </Main>
    );
}
